import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { Button } from '@/components/ui/button';
import CopilotChatPopup from '@/components/ui/copilot-chat-popup';
import assetService from '@/services/AssetService';
import { useBackendSubscription } from '@/hooks/useBackendSubscription';
import { useClerk, useUser } from '@clerk/clerk-react';
import {
  BarChart,
  Building,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileText,
  LayoutDashboard,
  LogOut,
  Settings,
  Users,
  Wallet,
  PieChart,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { Sidebar } from './Sidebar';

export function DashboardLayout() {
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const { signOut } = useClerk();
  const { user } = useUser();
  const { isPro, isPremium } = useBackendSubscription();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [assetContext, setAssetContext] = useState<
    | {
        type: 'property';
        assetId: number;
        assetName: string;
      }
    | undefined
  >(undefined);

  // Fetch asset context when on asset detail pages
  useEffect(() => {
    const fetchAssetContext = async () => {
      // Check if we're on an asset detail page
      if (
        location.pathname.includes('/assets/') &&
        slug &&
        !location.pathname.includes('/edit/')
      ) {
        try {
          const assets = await assetService.getAssets();
          const matchedAsset = assets.find((a) => a.slug === slug);
          if (matchedAsset) {
            setAssetContext({
              type: 'property',
              assetId: matchedAsset.id,
              assetName: matchedAsset.name,
            });
            console.log('[MainLayout] Fetched asset context:', {
              type: 'property',
              assetId: matchedAsset.id,
              assetName: matchedAsset.name,
            });
          }
        } catch (error) {
          console.error('Error fetching asset context:', error);
        }
      } else {
        // Clear context when not on asset detail page
        setAssetContext(undefined);
      }
    };

    fetchAssetContext();
  }, [location.pathname, slug]);

  console.log('[MainLayout] assetContext for chat:', assetContext);

  const handleLogout = () => {
    signOut();
  };

  const navLinks = [
    {
      to: '/dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard className="h-6 w-6" />,
    },
    { to: '/assets', label: 'Assets', icon: <Building className="h-6 w-6" /> },
    { to: '/contacts', label: 'Contacts', icon: <Users className="h-6 w-6" /> },
    {
      to: '/quick-reports',
      label: 'Quick Reports',
      icon: <BarChart className="h-6 w-6" />,
    },
    {
      to: '/bank-accounts',
      label: 'Bank Accounts',
      icon: <Wallet className="h-6 w-6" />,
    },
    {
      to: '/quickbooks',
      label: 'QuickBooks',
      icon: <Building2 className="h-6 w-6" />,
      feature: 'quickbooks', // Gate this nav item
    },
    {
      to: '/statements',
      label: 'Statements',
      icon: <PieChart className="h-6 w-6" />,
    },
    {
      to: '/documents',
      label: 'Documents',
      icon: <FileText className="h-6 w-6" />,
    },
    {
      to: '/settings',
      label: 'Settings',
      icon: <Settings className="h-6 w-6" />,
    },
  ];

  return (
    <div className="flex min-h-screen">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden border-r bg-background transition-all duration-300 md:block ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className="flex h-full flex-col gap-2 p-4">
          <div className="flex items-center justify-between px-2 py-4">
            {!isCollapsed ? (
              <div className="flex flex-col gap-1 items-start">
                <img
                  src="/logo.png"
                  alt="BetterCapital.US Logo"
                  className="h-8 mb-2"
                />
                <span className="text-sm text-muted-foreground">
                  {user?.primaryEmailAddress?.emailAddress}
                </span>
              </div>
            ) : (
              <img
                src="/logo-only.png"
                alt="BetterCapital.US Logo Only"
                className="h-8"
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </Button>
          </div>
          <nav className="flex flex-1 flex-col gap-2">
            {navLinks
              .filter((link) => {
                // Filter out features user doesn't have access to
                if (link.feature === 'quickbooks') {
                  return isPro || isPremium;
                }
                return true;
              })
              .map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all hover:bg-accent ${
                    location.pathname === link.to ||
                  location.pathname.startsWith(`${link.to}/`)
                    ? 'bg-accent'
                    : ''
                }`}
              >
                {link.icon}
                {!isCollapsed && <span>{link.label}</span>}
              </Link>
            ))}
          </nav>
          <div className="mt-auto">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all hover:bg-accent"
            >
              <LogOut className="h-6 w-6" />
              {!isCollapsed && <span>Logout</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <div className="md:hidden">
        <Sidebar />
      </div>

      {/* Main Content */}
      <main className={'flex-1 p-4 md:p-6'}>
        <div className="mb-4">
          <Breadcrumbs />
        </div>
        <Outlet />
      </main>

      {/* Floating AI Assistant */}
      <CopilotChatPopup showFloatingButton={true} context={assetContext} />
    </div>
  );
}
